import React from 'react';

import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {stepIsConfigEditorSupported} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';
import {PipelineStep, pipelineStepIsStepType} from 'pipelines/PipelineTypes';
import {getOutputStepDataRawDatas, inputStepDatasAreComplete} from 'pipelines/StepDataUtils';
import Float from 'ui-library/floating-ui/Float';

import {DEFAULT_FLOAT_PROPS} from './PopoverConstants';
import {RawDataPopoverContents} from './RawDataPopover';
import TableCreationPopoverContents, {
  TABLE_CREATION_POPOVER_SUPPORTED_STEP_TYPES,
} from './TableCreationPopoverContents';
import TransformPopoverContents from './TransformPopoverContents';
import UserDefinedFunctionPopoverContents from './UserDefinedFunctionPopoverContents';

function getStepPopoverContentsRenderFn(step: PipelineStep): (() => JSX.Element) | null {
  const {config, stepType, statusCode} = step;

  // Defensively, don't try to render content for steps with incomplete input
  if (!inputStepDatasAreComplete(step) || config == null) {
    return null;
  }

  // Step-type-specific popovers take priority over the generic transform popover.
  if (step.stepType === PipelineStepType.USER_DEFINED_FUNCTION) {
    return () => <UserDefinedFunctionPopoverContents step={step} />;
  }

  if (TABLE_CREATION_POPOVER_SUPPORTED_STEP_TYPES.has(stepType)) {
    return () => <TableCreationPopoverContents step={step} />;
  }

  if (
    pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, step) ||
    pipelineStepIsStepType(PipelineStepType.REGRESSION, 0, step)
  ) {
    if (statusCode !== PipelineStepStatusCode.SUCCEEDED) {
      return null;
    }
    const rawDatas = getOutputStepDataRawDatas(step, 'Output chart');
    return rawDatas.length > 0 ? () => <RawDataPopoverContents rawData={rawDatas[0]} /> : null;
  }

  if (pipelineStepIsStepType(PipelineStepType.TABLE_VIEW, 0, step)) {
    if (statusCode !== PipelineStepStatusCode.SUCCEEDED) {
      return null;
    }
    const rawDatas = getOutputStepDataRawDatas(step, 'Output dataset');
    return rawDatas.length > 0 ? () => <RawDataPopoverContents rawData={rawDatas[0]} /> : null;
  }

  // Generic transform popover for any remaining config-editor-supported steps.
  if (stepIsConfigEditorSupported(step)) {
    return () => <TransformPopoverContents step={step} />;
  }

  return null;
}

interface StepPopoverProps {
  step: PipelineStep;
  children: JSX.Element;
  disabled?: boolean;
}

/**
 * Show a popover with details about a step.
 *
 * Popover contents are different for each step type, and not all step types have content.
 *
 * Popover contents should be read-only.
 */
export default function StepPopover({step, children, disabled}: StepPopoverProps): JSX.Element {
  const renderContentsFn = getStepPopoverContentsRenderFn(step);

  if (disabled || renderContentsFn == null) {
    return <div className="u-cursor-default">{children}</div>;
  }

  return (
    <Float {...DEFAULT_FLOAT_PROPS} renderContent={renderContentsFn}>
      <div className="u-pointer">{children}</div>
    </Float>
  );
}
